import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Sensor, SensorCard, SensorStatus } from '@/components/SensorCard/SensorCard';
import { SensorScreenContent } from '@/components/SensorScreenContent/SensorScreenContent';
import { Stepper } from '@/components/Stepper/Stepper';

import { mergeClassNames } from '@/utils';
import Logger from '@/utils/CapcitorLog';
import { safePromise } from '@/utils/safePromise';

import { sensorService } from '@/controllers/sensorService';

import { useLowBatteryDevices } from '@/hooks/useLowBatteryDevices';
import { useSensorLayout } from '@/hooks/useSensorLayout';
import { useSensors } from '@/hooks/useSensors';
import { useSessionScreenStore } from '@/hooks/useSessionScreenStore';

import { useSensorsContext } from '@/contexts/SensorsContext';

import styles from './SensorScreen.module.css';

interface SensorScreenProps {
  isHubFlow: boolean;
  isRecalibration?: boolean;
}

const RECALIBRATION_STEP = 2;
const PLACEMENT_STEP = 1;

export const SensorScreen = ({ isHubFlow, isRecalibration }: SensorScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lowBatteryDevices = useLowBatteryDevices();

  const { state, resetCalibration, setAllSynced } = useSensorsContext();
  const { allConnected, allSuccessfullyCalibrated, sensorsInDatabase, allSynced, isCalibrating } = state;
  const { searchAndConnect } = useSensors(isHubFlow);

  const { goToNextScreen, goToScreen } = useSessionScreenStore();

  const [selectedSensor, setSelectedSensor] = useState<Sensor>();
  const sensorLayout = useSensorLayout(sensorsInDatabase, isHubFlow);

  const isPlacementStep = useMemo(() => currentStep === 1, [currentStep]);
  const steps = useMemo(
    () => [
      {
        name: 'Connect sensors',
        title: 'Connect sensors',
        description: 'Turn on the sensors to allow for connection',
        isNextDisabled: !(allConnected && (!isHubFlow || allSynced)),
      },
      {
        name: 'Place sensors',
        title: 'Place sensors',
        description: 'Place sensors on the body according to the labels on the sensors',
        isNextDisabled: false,
      },
      {
        name: 'Calibrate',
        title: 'Calibrate',
        description: 'Stand still in a neutral position for calibration',
        isPreviousDisabled: isCalibrating,
        isNextDisabled: !allSuccessfullyCalibrated,
      },
    ],
    [allConnected, allSuccessfullyCalibrated, allSynced, isCalibrating],
  );

  const handleStepChange = (step: number) => {
    setSelectedSensor(undefined);

    if (step < currentStep) {
      // We want to reset the calibration data
      resetCalibration();
      setIsNextDisabled(false);
    }

    if (step === steps.length) {
      if (isRecalibration) {
        goToScreen(RECALIBRATION_STEP);
      } else {
        goToNextScreen();
      }
    }

    // If the step is equal to 'Place sensors', we want to disable the next button
    if (step === PLACEMENT_STEP) {
      setIsNextDisabled(true);

      // TODO: This should ideally be exposed from the swift layer
      // ? When all sensors are ready to stream we can enable the next button
      timeoutRef.current = setTimeout(() => {
        setIsNextDisabled(false);
      }, 4000);
    }

    setCurrentStep(step);
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    async function playAnimation() {
      if (!selectedSensor) {
        return;
      }

      const ANIMATION_DURATION = 1500;

      interval = setInterval(() => {
        safePromise(sensorService.playAnimation(selectedSensor.id, 255, 20, 147, 'blink'));
      }, ANIMATION_DURATION);
    }

    playAnimation();

    return () => {
      clearInterval(interval);
    };
  }, [selectedSensor]);

  const handleSensorSelect = async (sensor: Sensor) => {
    if (!isPlacementStep) {
      return setSelectedSensor(undefined);
    }

    if (selectedSensor?.id === sensor.id) {
      setSelectedSensor(undefined);
    } else {
      setSelectedSensor(sensor);
    }
  };

  useEffect(() => {
    if (sensorsInDatabase.length > 0) {
      searchAndConnect(sensorsInDatabase.map((sensor) => sensor.id));
    }
  }, [sensorsInDatabase]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const activeStep = useMemo(() => {
    if (isRecalibration) {
      return RECALIBRATION_STEP;
    }

    return currentStep;
  }, [currentStep, isRecalibration]);

  return (
    <IonGrid className={mergeClassNames('ion-no-padding', styles.grid)} fixed>
      <IonRow className={styles.topArea}>
        <IonCol className={styles.left} size='4'>
          {sensorLayout?.left.map((sensor) => (
            <SensorCard
              id={sensor.id}
              isHubFlow={isHubFlow}
              isOnCalibrationScreen={activeStep === RECALIBRATION_STEP}
              key={sensor.id}
              onSelect={handleSensorSelect}
              segment={sensor.mappedSegment}
              selected={selectedSensor?.id === sensor.id}
              state={SensorStatus.CONNECTED_NOT_CALIBRATED}
              title={sensor.title}
            />
          ))}
        </IonCol>

        <SensorScreenContent
          currentStep={activeStep}
          isHubFlow={isHubFlow}
          selectedSensor={selectedSensor}
          setAllSynced={setAllSynced}
          state={state}
        />

        <IonCol className={styles.right} size='4'>
          {sensorLayout?.right.map((sensor) => (
            <SensorCard
              id={sensor.id}
              isHubFlow={isHubFlow}
              isOnCalibrationScreen={activeStep === RECALIBRATION_STEP}
              key={sensor.id}
              onSelect={handleSensorSelect}
              segment={sensor.mappedSegment}
              selected={selectedSensor?.id === sensor.id}
              state={SensorStatus.CONNECTED_NOT_CALIBRATED}
              title={sensor.title}
            />
          ))}
        </IonCol>
      </IonRow>

      <IonRow className={styles.bottomArea}>
        <IonCol size='12'>
          <Stepper
            currentStep={activeStep}
            isNextDisabled={steps[activeStep].isNextDisabled || isNextDisabled || lowBatteryDevices.length > 0}
            isPreviousDisabled={steps[activeStep].isPreviousDisabled || isRecalibration}
            onStepChange={handleStepChange}
            steps={steps}
          />
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};
